import React, { useState } from 'react';
import {
	Box,
	Typography,
	Grid,
	Paper,
	Breadcrumbs,
	Link,
} from '@mui/material';
import {
	Extension as ExtensionIcon,
	Security as SecurityIcon,
	AccountTree as RecordTypeIcon,
} from '@mui/icons-material';
import { PackageInsight } from './PackageInsight';
import { PermissionsInsight } from './PermissionsInsight';
import { RecordTypeInsight } from './RecordTypeInsight';

interface InsightsProps {
	snapshotId: string;
	workspaceId: string;
}

type ActiveInsight = null | 'packages' | 'permissions' | 'record_types';

export const Insights: React.FC<InsightsProps> = ({ snapshotId, workspaceId }) => {
	const [activeInsight, setActiveInsight] = useState<ActiveInsight>(null);

	if (activeInsight === 'packages') {
		return (
			<Box sx={{ flex: 1, p: 3 }}>
				<Breadcrumbs sx={{ mb: 2 }}>
					<Link
						component="button"
						underline="hover"
						color="inherit"
						onClick={() => setActiveInsight(null)}
						sx={{ cursor: 'pointer', fontSize: '0.95rem' }}
					>
						Insights
					</Link>
					<Typography color="text.primary" sx={{ fontSize: '0.95rem' }}>
						Installed Packages
					</Typography>
				</Breadcrumbs>
				<PackageInsight snapshotId={snapshotId} />
			</Box>
		);
	}

	if (activeInsight === 'permissions') {
		return (
			<Box sx={{ flex: 1, p: 3 }}>
				<Breadcrumbs sx={{ mb: 2 }}>
					<Link
						component="button"
						underline="hover"
						color="inherit"
						onClick={() => setActiveInsight(null)}
						sx={{ cursor: 'pointer', fontSize: '0.95rem' }}
					>
						Insights
					</Link>
					<Typography color="text.primary" sx={{ fontSize: '0.95rem' }}>
						Permissions
					</Typography>
				</Breadcrumbs>
				<PermissionsInsight snapshotId={snapshotId} />
			</Box>
		);
	}

	if (activeInsight === 'record_types') {
		return (
			<Box sx={{ flex: 1, p: 3 }}>
				<Breadcrumbs sx={{ mb: 2 }}>
					<Link
						component="button"
						underline="hover"
						color="inherit"
						onClick={() => setActiveInsight(null)}
						sx={{ cursor: 'pointer', fontSize: '0.95rem' }}
					>
						Insights
					</Link>
					<Typography color="text.primary" sx={{ fontSize: '0.95rem' }}>
						Record Types
					</Typography>
				</Breadcrumbs>
				<RecordTypeInsight snapshotId={snapshotId} />
			</Box>
		);
	}

	return (
		<Box sx={{ flex: 1, p: 3 }}>
			<Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
				Insights
			</Typography>
			<Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
				Click a tile to explore detailed analysis results.
			</Typography>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<Paper
						onClick={() => setActiveInsight('packages')}
						sx={{
							p: 3,
							height: 150,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1,
							cursor: 'pointer',
							transition: 'all 0.15s ease-in-out',
							'&:hover': {
								backgroundColor: 'action.hover',
								transform: 'translateY(-2px)',
								boxShadow: 3,
							},
						}}
						variant="outlined"
					>
						<ExtensionIcon sx={{ fontSize: 36, color: 'primary.main' }} />
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							Installed Packages
						</Typography>
						<Typography variant="caption" color="text.secondary" textAlign="center">
							Package inventory and version analysis
						</Typography>
					</Paper>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<Paper
						onClick={() => setActiveInsight('permissions')}
						sx={{
							p: 3,
							height: 150,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1,
							cursor: 'pointer',
							transition: 'all 0.15s ease-in-out',
							'&:hover': {
								backgroundColor: 'action.hover',
								transform: 'translateY(-2px)',
								boxShadow: 3,
							},
						}}
						variant="outlined"
					>
						<SecurityIcon sx={{ fontSize: 36, color: 'primary.main' }} />
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							Permissions
						</Typography>
						<Typography variant="caption" color="text.secondary" textAlign="center">
							Profile user assignments and permissions
						</Typography>
					</Paper>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<Paper
						onClick={() => setActiveInsight('record_types')}
						sx={{
							p: 3,
							height: 150,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1,
							cursor: 'pointer',
							transition: 'all 0.15s ease-in-out',
							'&:hover': {
								backgroundColor: 'action.hover',
								transform: 'translateY(-2px)',
								boxShadow: 3,
							},
						}}
						variant="outlined"
					>
						<RecordTypeIcon sx={{ fontSize: 36, color: 'primary.main' }} />
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							Record Types
						</Typography>
						<Typography variant="caption" color="text.secondary" textAlign="center">
							Record type distribution and record counts
						</Typography>
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);
};
